// Product page behaviour; markup and the pd-data JSON come from sections/stally-rd-product*.liquid.
const cartUrl = path => `${window.Shopify?.routes?.root || '/'}${path}`;
const escapeHTML = value => String(value).replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
// a shopper-usage count, sent by stally-rd.js; one for a tap that leaves the page is sent by the next page
const trackUse = (name, leaving = false) => document.dispatchEvent(new CustomEvent(leaving ? 'stally:track-next' : 'stally:track', { detail: name }));
// a plain click on a link leaves the page; with ctrl, cmd, shift or alt it usually opens elsewhere and this page stays
// (Firefox follows an alt-click). A click another script cancelled (none does today) is handed off too: that script
// may navigate itself, and if this page stays, the tab's next page sends the count.
const leaves = event => !(event.ctrlKey || event.metaKey || event.shiftKey || event.altKey);
// Shopify's image CDN resizes by its width parameter
const resized = (src, width) => {
  try {
    const url = new URL(src, location.href);
    url.searchParams.set('width', width);
    return url.href;
  } catch {
    return src;
  }
};
// The large photo, and the rail beside it (a row of four under it on phones); see the gallery comment in the section.
const RAIL_LENGTH = 4;
const MAIN_SIZES = '(min-width: 1500px) 640px, (min-width: 993px) 42vw, (min-width: 651px) 70vw, 100vw';
const RAIL_SIZES = '(min-width: 1500px) 170px, (min-width: 993px) 11vw, (min-width: 651px) 18vw, 25vw';
const LIGHTBOX_WIDTHS = [900, 1400, 2000];
const motion = () => (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth');
// the current product section's gallery opener (initProduct sets it), for the Details matter and Stallys in the Wild
// photos below
let openGallery = null;

function initProduct(root = document) {
  const get = id => root.querySelector(`#${id}`);
  const data = JSON.parse(get('pd-data')?.textContent || 'null');
  const form = get('pd-form');
  if (!data || !form) return;
  const { colorIndex, sizeIndex, variants } = data;
  let color = data.color;
  let size = data.size;
  let busy = false;
  // the "One size" button of a single-variant product is display only: always chosen, never redrawn
  const sizeButtons = [...root.querySelectorAll('#pd-sizes .size-choice:not([data-one-size])')];
  const swatchButtons = [...root.querySelectorAll('#pd-swatches button.swatch')];
  const gallery = get('pd-gallery');
  const figures = [...gallery.querySelectorAll('figure')];
  const add = get('pd-add'), notify = get('pd-notify'), status = get('pd-status'), variantInput = get('pd-variant-id');
  const preorder = get('pd-preorder');
  const finalSale = get('pd-final-sale');
  const left = get('pd-left');
  const colorMatches = v => colorIndex === null || v.options[colorIndex] === color;
  const sizeVariant = name => variants.find(v => colorMatches(v) && v.options[sizeIndex] === name);
  const selected = () => variants.find(v => colorMatches(v) && (sizeIndex === null || v.options[sizeIndex] === size));
  const colorInStock = () => variants.some(v => colorMatches(v) && v.available);
  if (sizeIndex !== null && sizeButtons.length === 1) size = sizeButtons[0].dataset.size;
  // ?choose_size (the hero, tiles, set rows, homepage cards and quick shop): ?variant= has the server draw that color, and
  // the shopper still picks the size. A color sold out since the link was made opens on Sold out and Notify me.
  else if (sizeIndex !== null && new URLSearchParams(location.search).has('choose_size') && colorInStock()) size = null;
  // A sold-out color shows Sold out and Notify me on a size the shopper hasn't picked here: an in-stock color drops it
  // again.
  let sizeAssumed = sizeIndex !== null && sizeButtons.length > 1 && !colorInStock();

  // Figures are re-parented rather than hidden so the CSS :only-child rule still sees a lone photo.
  function drawGallery() {
    const matching = colorIndex === null ? figures : [...figures.filter(f => f.dataset.color === color), ...figures.filter(f => !f.dataset.color)];
    const shown = (matching.length ? matching : figures).slice(0, data.photosPerColor);
    shown.forEach(f => { f.hidden = false; });
    gallery.replaceChildren(...shown);
    const firstImage = gallery.querySelector('img');
    if (firstImage) { firstImage.loading = 'eager'; firstImage.fetchPriority = 'high'; }
    drawRail();
  }

  // With more photos than slots, the fourth rail photo shows "+N more", N counting itself.
  function drawRail() {
    const shown = [...gallery.querySelectorAll('figure:not([hidden])')];
    shown.forEach((figure, index) => {
      const slot = index === 0 ? 'main' : index <= RAIL_LENGTH ? 'rail' : 'rest';
      figure.dataset.slot = slot;
      const image = figure.querySelector('img');
      if (image) image.sizes = slot === 'main' ? MAIN_SIZES : RAIL_SIZES;
      const more = index === RAIL_LENGTH && shown.length > RAIL_LENGTH + 1 ? shown.length - RAIL_LENGTH : 0;
      figure.toggleAttribute('data-more', more > 0);
      const label = figure.querySelector('.pd-more');
      if (label) label.innerHTML = more ? `<b>+${more}</b> more` : '';
      figure.querySelector('.pd-open')?.setAttribute('aria-label', `${image?.alt || 'Photo'}, photo ${index + 1} of ${shown.length}${more ? `, +${more} more` : ''}`);
    });
  }

  // Lightbox slides are built on open, so they follow the chosen color (or the photo set opened from).
  const lightbox = get('pd-lightbox');
  const track = get('pd-lb-track');
  const thumbs = get('pd-lb-thumbs');
  const counter = get('pd-lb-count');
  // A smooth goTo() passes the slides in between, so while it runs (`landing`) scroll events don't move the counter.
  // The slide the track rests on at scrollend, or after SETTLE_MS with no scroll event (no scrollend), is current.
  const SETTLE_MS = 160;
  let current = -1;
  let landing = null;
  let settleTimer = 0;
  const pad = number => String(number).padStart(2, '0');
  const slideAt = () => (track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0);
  function setCurrent(index) {
    const total = track.children.length;
    const next = Math.max(0, Math.min(index, total - 1));
    const changed = next !== current;
    current = next;
    // written only when it changes, so the polite live region announces each photo once
    const text = `${pad(current + 1)} / ${pad(total)}`;
    if (counter.textContent !== text) counter.textContent = text;
    if (!changed) return;
    thumbs.querySelectorAll('.pd-lb-thumb').forEach((thumb, position) => {
      if (position === current) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    // Tab reaches the shown photo's model size ⓘ only: focusing another slide's would scroll the track to it. Focus left
    // on a photo that slid away goes back to the track.
    const slide = track.children[current];
    track.querySelectorAll('.rd-tip-btn').forEach(button => { button.tabIndex = slide?.contains(button) ? 0 : -1; });
    if (track.contains(document.activeElement) && document.activeElement !== track && !slide?.contains(document.activeElement)) track.focus({ preventScroll: true });
    // aria-disabled, not disabled: a disabled button would drop keyboard focus out of the dialog
    lightbox.querySelector('.pd-lb-prev').setAttribute('aria-disabled', String(current === 0));
    lightbox.querySelector('.pd-lb-next').setAttribute('aria-disabled', String(current === total - 1));
    const thumb = thumbs.children[current];
    if (thumb && thumbs.scrollWidth > thumbs.clientWidth) thumbs.scrollTo({ left: thumb.offsetLeft - (thumbs.clientWidth - thumb.offsetWidth) / 2, behavior: motion() });
  }
  function settle() {
    clearTimeout(settleTimer);
    landing = null;
    // closing hides the track and resets its scroll: the closed gallery keeps the photo it showed
    if (lightbox.open) setCurrent(slideAt());
  }
  function goTo(index, behavior = motion()) {
    const target = Math.max(0, Math.min(index, track.children.length - 1));
    clearTimeout(settleTimer);
    if (behavior === 'instant' || target === slideAt()) {
      // nothing to animate (or no motion wanted): no scroll to wait for
      landing = null;
      track.scrollTo({ left: target * track.clientWidth, behavior: 'instant' });
      setCurrent(target);
      return;
    }
    landing = target;
    track.scrollTo({ left: target * track.clientWidth, behavior });
    setCurrent(target);
    // if the scroll never starts, nothing else would end the wait
    settleTimer = setTimeout(settle, SETTLE_MS * 4);
  }
  // A gallery photo with the model's size ⓘ (snippets/stally-rd-model-info.liquid) brings a copy of it, boxed at the
  // photo's shape so it sits on the photo's corner; its behaviour is in stally-rd.js.
  function modelPhoto(image, photo, index) {
    const figure = image.closest('#pd-gallery figure');
    const tip = figure?.querySelector('.rd-tip')?.cloneNode(true);
    if (!tip) return photo;
    const id = `pd-lb-model-${index}`;
    tip.querySelector('.rd-tip-btn').setAttribute('aria-describedby', id);
    Object.assign(tip.querySelector('.rd-tip-text'), { id, hidden: true });
    const ratio = Number(figure.dataset.ratio) || 0.8;
    return `<span class="pd-lb-photo" style="--ratio:${ratio}">${photo}${tip.outerHTML}</span>`;
  }
  function openLightbox(start, photos = [...gallery.querySelectorAll('figure:not([hidden]) img')]) {
    if (!lightbox || !photos.length) return;
    track.innerHTML = photos.map((image, index) => {
      const src = image.currentSrc || image.src;
      const photo = `<img src="${escapeHTML(resized(src, 1400))}" srcset="${escapeHTML(LIGHTBOX_WIDTHS.map(width => `${resized(src, width)} ${width}w`).join(', '))}" sizes="min(100vw, 80dvh)" alt="${escapeHTML(image.alt)}" width="1200" height="1500"${index === start ? '' : ' loading="lazy"'}>`;
      return `<figure class="pd-lb-slide" role="group" aria-roledescription="slide" aria-label="${index + 1} of ${photos.length}">${modelPhoto(image, photo, index)}</figure>`;
    }).join('');
    thumbs.innerHTML = photos.map((image, index) => `<button type="button" class="pd-lb-thumb" data-index="${index}" aria-label="Show photo ${index + 1}"><img src="${escapeHTML(resized(image.currentSrc || image.src, 160))}" alt="" width="120" height="150" loading="lazy"></button>`).join('');
    // one photo: no prev/next or thumbnails to tab through
    lightbox.toggleAttribute('data-single', photos.length < 2);
    current = -1;
    lightbox.showModal();
    goTo(start, 'instant');
  }
  openGallery = openLightbox;
  gallery.addEventListener('click', event => {
    const opener = event.target.closest('.pd-open');
    if (!opener) return;
    const shown = [...gallery.querySelectorAll('figure:not([hidden])')];
    openLightbox(Math.max(0, shown.indexOf(opener.closest('figure'))));
  });
  if (lightbox) {
    lightbox.addEventListener('close', () => {
      clearTimeout(settleTimer);
      landing = null;
    });
    lightbox.addEventListener('click', event => {
      const step = event.target.closest('[data-step]');
      if (step) goTo(current + Number(step.dataset.step));
      const thumb = event.target.closest('.pd-lb-thumb');
      if (thumb) goTo(Number(thumb.dataset.index));
    });
    lightbox.addEventListener('keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      goTo(current + (event.key === 'ArrowRight' ? 1 : -1));
    });
    // a swipe or trackpad scroll moves the counter as it goes; during a goTo() each scroll event restarts the wait
    let frame = 0;
    track.addEventListener('scroll', () => {
      if (!lightbox.open) return;
      if (landing !== null) {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(settle, SETTLE_MS);
        return;
      }
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setCurrent(slideAt()));
    });
    track.addEventListener('scrollend', settle);
    // a resize leaves the track between slides: put the current one back
    new ResizeObserver(() => { if (lightbox.open) track.scrollTo({ left: current * track.clientWidth, behavior: 'instant' }); }).observe(track);
  }

  // userChange: only a shopper's choice writes ?variant= into the URL, keeping every other parameter (utm, fbclid).
  function drawOptions(userChange = false) {
    if (colorIndex !== null) get('pd-color').textContent = color;
    swatchButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.color === color)));
    sizeButtons.forEach(button => {
      const variant = sizeVariant(button.dataset.size);
      button.disabled = !variant;
      button.toggleAttribute('data-sold-out', Boolean(variant && !variant.available));
      button.setAttribute('aria-label', `${button.dataset.size}${variant && !variant.available ? ', sold out' : ''}`);
      button.setAttribute('aria-pressed', String(button.dataset.size === size));
    });
    const variant = selected();
    if (variant) {
      get('pd-price').textContent = variant.price;
      // Shopify's accelerated checkout and its installments line (a form of its own under the price) follow their
      // form's id input by its change event
      for (const input of [variantInput, get('pd-installments-variant')]) {
        if (!input || input.value === String(variant.id)) continue;
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    if (userChange) {
      const url = new URL(location.href);
      // no size yet: the color's link, as on a tile, so a reload or a shared link keeps the color with no size picked
      url.searchParams.set('variant', (variant || variants.find(v => colorMatches(v) && v.available) || variants.find(colorMatches)).id);
      // an assumed size stays unpicked in the link too, for when that color restocks
      if (variant && !sizeAssumed) url.searchParams.delete('choose_size');
      else url.searchParams.set('choose_size', '');
      history.replaceState(history.state, '', url);
    }
    // a pre-order's "N left" beside the price is the chosen variant's: hidden before a size is chosen and at 0
    // (sections/stally-rd-product.liquid)
    if (left) {
      left.hidden = !(variant?.left > 0);
      if (variant?.left > 0) left.textContent = `${variant.left} left`;
    }
    const soldOut = Boolean(variant && !variant.available);
    const express = root.querySelector('.pd-express');
    if (express) express.hidden = !variant || soldOut;
    // not disabled while adding, so keyboard focus stays on the button; the busy flag blocks repeats
    add.disabled = !variant || soldOut;
    add.textContent = !variant ? data.selectLabel : soldOut ? 'Sold out' : data.addLabel;
    // a sold-out pre-order hides its ship line, also before a size is chosen when the color has none left (as the
    // server renders it); a hidden line must not describe the button. The final-sale line never hides.
    if (preorder) preorder.hidden = soldOut || !colorInStock();
    if (preorder || finalSale) {
      const described = [finalSale, preorder].filter(line => line && !line.hidden).map(line => line.id).join(' ');
      if (described) add.setAttribute('aria-describedby', described);
      else add.removeAttribute('aria-describedby');
    }
    if (notify) {
      notify.hidden = !soldOut;
      if (variant) {
        notify.dataset.variantId = variant.id;
        notify.dataset.variantTitle = variant.title;
      }
    }
  }

  // "Details matter" and "Stallys in the Wild" (sections/stally-rd-product-details.liquid, -wild.liquid):
  // data-color="" marks the set for every color.
  function drawDetails() {
    document.querySelectorAll('[data-details]').forEach(section => {
      const sets = [...section.querySelectorAll('.pd-detail-set')];
      const match = sets.find(set => set.dataset.color === color) || sets.find(set => !set.dataset.color);
      sets.forEach(set => { set.hidden = set !== match; });
      section.hidden = !match;
    });
  }

  get('pd-swatches')?.addEventListener('click', event => {
    // a hat's swatches are links to its other colors: nothing to redraw (counted with the other links, below)
    const swatch = event.target.closest('button.swatch');
    if (!swatch) return;
    color = swatch.dataset.color;
    if (sizeIndex !== null && (size === null || sizeAssumed)) {
      sizeAssumed = !colorInStock();
      size = sizeAssumed ? variants.find(colorMatches).options[sizeIndex] : null;
    }
    drawGallery();
    drawDetails();
    drawOptions(true);
    trackUse('stally_swatch_tap');
  });
  get('pd-sizes')?.addEventListener('click', event => {
    const button = event.target.closest('.size-choice');
    if (!button || button.disabled || button.hasAttribute('data-one-size')) return;
    size = button.dataset.size;
    sizeAssumed = false;
    status.textContent = '';
    drawOptions(true);
  });
  // The size guide button opens this page's Size Guide accordion and brings it into view, with focus on its summary;
  // without a chart (hats) the button links to the size guide page instead, and none of this runs.
  const guideButton = root.querySelector('.pd-size-guide[href="#pd-size-guide"]');
  // opens and scrolls to the accordion; returns its summary for the caller to focus
  const openGuide = event => {
    const guide = get('pd-size-guide');
    if (!guide) return null;
    event.preventDefault();
    guide.open = true;
    guide.scrollIntoView({ behavior: motion(), block: 'start' });
    return guide.querySelector('summary');
  };
  guideButton?.addEventListener('click', event => openGuide(event)?.focus({ preventScroll: true }));
  // Enter opens on keydown but moves focus on keyup: focused on keydown, a held Enter's repeats would reach the summary
  // and toggle it
  let enterPending = false;
  guideButton?.addEventListener('keydown', event => { if (event.key === 'Enter') enterPending = Boolean(openGuide(event)); });
  guideButton?.addEventListener('keyup', event => {
    if (event.key !== 'Enter' || !enterPending) return;
    enterPending = false;
    get('pd-size-guide').querySelector('summary').focus({ preventScroll: true });
  });
  // focus left before the keyup (Tab with Enter held): a later stray keyup must not pull focus to the summary
  guideButton?.addEventListener('blur', () => { enterPending = false; });
  // opened from the button or its own summary; another accordion opening closes it (toggle with open false)
  get('pd-size-guide')?.addEventListener('toggle', event => { if (event.target.open) trackUse('stally_size_guide_open'); });

  // Gift cards: the recipient fields post only while "send as a gift" is ticked, as Shopify expects.
  const recipientToggle = get('pd-recipient-toggle');
  // the no-JS fallback field would post a second value; with JS the checkbox decides (as Concept's recipient-form.js)
  const recipientControl = get('pd-recipient-control');
  if (recipientControl) recipientControl.disabled = true;
  recipientToggle?.addEventListener('change', () => {
    const fields = get('pd-recipient-fields');
    fields.hidden = !recipientToggle.checked;
    fields.querySelectorAll('input, textarea').forEach(field => { field.disabled = !recipientToggle.checked; });
    get('pd-recipient-offset').value = new Date().getTimezoneOffset();
  });

  form.addEventListener('submit', async event => {
    event.preventDefault();
    const variant = selected();
    if (busy || !variant?.available) return;
    busy = true;
    add.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(cartUrl('cart/add.js'), { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      const json = await response.json();
      if (!response.ok) throw new Error(json.description || json.message || `Add to bag failed: ${response.status}`);
      const cart = await (await fetch(cartUrl('cart.js'), { headers: { Accept: 'application/json' } })).json();
      document.dispatchEvent(new CustomEvent('stally:cart', { detail: cart }));
      // design copy on the copy sheet: reword only there
      status.innerHTML = `Added to your rotation. <a href="${cartUrl('cart')}">View bag</a>`;
    } catch (error) {
      status.textContent = error.message;
    } finally {
      busy = false;
      add.removeAttribute('aria-busy');
      drawOptions();
    }
  });

  // Shop Promise's badge (Shopify's <delivery-promise-wc>, which its script adds to the form) also reads "Free shipping
  // over $99", so the section's shipping note hides while the badge shows: the buy area states it once. Where no badge
  // shows (the pre-order hat, Oct 27) the element stays 0px tall and the note stays.
  // shortcut: Shopify's tag name; if it changes, the note simply stays, as before.
  const shipNote = get('pd-ship-note');
  if (shipNote) {
    const badges = new Set();
    const badgeSize = new ResizeObserver(() => { shipNote.hidden = [...badges].some(badge => badge.isConnected && badge.offsetHeight > 0); });
    const watch = () => form.querySelectorAll('delivery-promise-wc').forEach(badge => {
      if (!badges.has(badge)) { badges.add(badge); badgeSize.observe(badge); }
    });
    new MutationObserver(watch).observe(form, { childList: true, subtree: true });
    watch();
  }

  drawGallery();
  drawOptions();
}

// "You may also like": Shopify's related products, rendered by the same section.
function initRecommendations(root = document) {
  const more = root.querySelector('[data-recommendations]');
  if (!more) return;
  fetch(more.dataset.recommendations)
    .then(response => response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`)))
    .then(html => {
      const cards = new DOMParser().parseFromString(html, 'text/html').getElementById('pd-more');
      if (cards?.children.length) more.querySelector('#pd-more').replaceChildren(...cards.children);
      else more.hidden = true;
    })
    .catch(error => { more.hidden = true; console.warn('You may also like: recommendations unavailable.', error); });
}

// A Details matter or Stallys in the Wild photo opens the gallery with every photo of its set, past the fifth tile
// too. One document handler, added once, so a section the theme editor reloads is still served.
document.addEventListener('click', event => {
  const opener = event.target.closest?.('.pd-detail-open');
  if (!opener || !openGallery) return;
  const photos = [...opener.closest('.pd-detail-set').querySelectorAll('figure img')];
  openGallery(Math.max(0, photos.indexOf(opener.querySelector('img'))), photos);
});

// Links that count a shopper's use, sent by the page they open: a hat's other colors, a cross-sell tile
// (sections/stally-rd-product-more.liquid in a cross-sell mode, not "You may also like"), and the size guide button of a
// product with no size chart (the link inside the opened accordion isn't a second open).
const linkUse = target => {
  if (target.closest?.('#pd-swatches a.swatch')) return 'stally_swatch_tap';
  if (target.closest?.('.pd-more[data-cross-sell] .pg-card')) return 'stally_xsell_click';
  const guide = target.closest?.('a.pd-size-guide:not([href="#pd-size-guide"])');
  return guide && !guide.closest('#pd-size-guide') ? 'stally_size_guide_open' : null;
};
document.addEventListener('click', event => {
  const name = linkUse(event.target);
  if (name) trackUse(name, leaves(event));
});
// a middle-click opens the link in a new tab and this page stays: sent at once (right-click is button 2)
document.addEventListener('auxclick', event => {
  const name = event.button === 1 && linkUse(event.target);
  if (name) trackUse(name);
});

initProduct();
initRecommendations();
// the theme editor re-renders a section without re-running scripts
document.addEventListener('shopify:section:load', event => {
  initProduct(event.target);
  initRecommendations(event.target);
});
